// Cours premium n°3 — "Décider • Planifier • S'adapter • Devenir autonome"
// Circulation A2 — 14 chapitres pédagogiques illustrés
// Cours 1: je découvre → Cours 2: j'observe et anticipe → Cours 3: je construis ma conduite

export const cours = {
  slug: 'troisieme-circulation-a2',
  title: 'Ma 3ᵉ circulation A2',
  subtitle: 'Décider • Planifier • S\'adapter • Devenir autonome',
  duree: '2 heures de conduite',
  mantra: 'VOIR → COMPRENDRE → PRÉVOIR → PRÉPARER',
  intro: "Sur cette séance de 2 heures, la radio devient moins directive. Tu dois progressivement construire ta conduite à partir de ce que tu observes, et pas seulement répondre aux indications de ton enseignant. Nous franchissons un nouveau palier : de « je sais faire » à « je sais pourquoi je le fais ».",
  sections: [
    {
      num: '01',
      kicker: 'Point de départ',
      title: 'Ton bilan devient ton point de départ',
      image: '/images/articles/cours3-c1.png',
      imageAlt: 'Ton bilan devient ton point de départ — Code 2 Roues',
      lead: 'Nous reprenons ton objectif fixé à la fin du cours 2 :',
      type: 'timeline',
      steps: [
        { time: 'Acquis', label: 'CE QUI EST SOLIDE', desc: 'Tu identifies les compétences que tu maîtrises désormais avec confiance.' },
        { time: 'Fragile', label: 'CE QUI RESTE À CONSOLIDER', desc: 'Tu repères ce qui demande encore de l\'attention ou de la répétition.' },
        { time: 'Cible', label: 'CE QUE TU VAS AMÉLIORER', desc: 'Tu définis ton objectif du jour, spécifique et mesurable.' },
      ],
      retenir: 'CHAQUE SÉANCE S\'APPUIE SUR LA PRÉCÉDENTE. AUJOURD\'HUI, TU AVANCES.',
    },
    {
      num: '02',
      kicker: 'Autonomie rapide',
      title: 'Tu prends rapidement ton autonomie',
      image: '/images/articles/cours3-c2.png',
      imageAlt: 'Tu prends rapidement ton autonomie — Code 2 Roues',
      lead: 'Cette fois, il n\'y a plus besoin d\'un long temps d\'adaptation. Dès les premiers kilomètres, tu dois retrouver tes automatismes :',
      type: 'list',
      list: [
        { label: 'OBSERVATION', desc: 'Ton regard s\'active immédiatement, loin devant.' },
        { label: 'ALLURE', desc: 'Adaptée à l\'environnement dès le départ.' },
        { label: 'POSITIONNEMENT', desc: 'Sur la voie, dans la circulation.' },
        { label: 'COMMUNICATION', desc: 'Clignotants, regards, signaux.' },
        { label: 'ANTICIPATION', desc: 'Tu prépares déjà ce qui arrive.' },
      ],
      retenir: 'TES ACQUIS DOIVENT ÊTRE DISPONIBLES DÈS LES PREMIERS MÈTRES.',
    },
    {
      num: '03',
      kicker: 'Regard loin',
      title: 'Tu construis ta conduite plus loin devant toi',
      image: '/images/articles/cours3-c3.png',
      imageAlt: 'Tu construis ta conduite plus loin devant toi — Code 2 Roues',
      lead: 'Tu ne conduis plus seulement en fonction du véhicule qui te précède. Ton regard va chercher plusieurs secondes et plusieurs situations en avant : ralentissement, feu, intersection, piéton, changement de voie, véhicule stationné, virage…',
      type: 'cards3',
      cards: [
        { title: 'VOIR', desc: 'Je porte mon regard loin devant.' },
        { title: 'COMPRENDRE', desc: 'J\'interprète ce que je vois.' },
        { title: 'PRÉVOIR & PRÉPARER', desc: 'Je construis ma réponse avant d\'y arriver.' },
      ],
      retenir: 'TU NE CONDUIS PLUS DANS LE PRÉSENT. TU CONDUIS LA SITUATION QUI ARRIVE.',
    },
    {
      num: '04',
      kicker: 'Hiérarchisation',
      title: 'Tu hiérarchises les informations',
      image: '/images/articles/cours3-c4.png',
      imageAlt: 'Tu hiérarchises les informations — Code 2 Roues',
      lead: 'Tout ce que tu vois n\'a pas la même importance. Tu apprends à distinguer trois niveaux :',
      type: 'cards3',
      cards: [
        { title: 'INFORMATION UTILE', desc: 'À mémoriser sans action immédiate.' },
        { title: 'RISQUE POTENTIEL', desc: 'À surveiller, à préparer.' },
        { title: 'DANGER IMMÉDIAT', desc: 'À traiter sans délai.' },
      ],
      retenir: 'NE PAS ÊTRE SUBMERGÉ, C\'EST SAVOIR OÙ PORTER TON ATTENTION EN PRIORITÉ.',
    },
    {
      num: '05',
      kicker: 'Risques multiples',
      title: 'Tu gères plusieurs risques en même temps',
      image: '/images/articles/cours3-c5.png',
      imageAlt: 'Tu gères plusieurs risques en même temps — Code 2 Roues',
      lead: 'Une situation réelle peut associer : véhicule devant + intersection + piéton + véhicule stationné + mauvaise visibilité. Tu apprends à te demander :',
      type: 'list',
      list: [
        { label: 'QUEL EST LE RISQUE PRINCIPAL ?', desc: 'Celui qui peut se réaliser en premier.' },
        { label: 'QUEL RISQUE PEUT APPARAÎTRE ENSUITE ?', desc: 'La situation qui suit celle-ci.' },
        { label: 'QUELLE MARGE DOIS-JE CONSERVER ?', desc: 'Espace, temps, options.' },
      ],
      retenir: 'GÉRER PLUSIEURS RISQUES, C\'EST GARDER LE CONTRÔLE DE LA SCÈNE.',
    },
    {
      num: '06',
      kicker: 'Positionnement',
      title: 'Tu choisis ton positionnement',
      image: '/images/articles/cours3-c6.png',
      imageAlt: 'Tu choisis ton positionnement — Code 2 Roues',
      lead: 'Ton placement n\'est plus simplement une règle à appliquer. Il devient un outil de sécurité que tu construis en fonction de :',
      type: 'list',
      list: [
        { label: 'MIEUX VOIR', desc: 'Ouvrir ton champ de vision.' },
        { label: 'ÊTRE VU', desc: 'Rester visible des autres usagers.' },
        { label: 'CONSERVER TES DISTANCES', desc: 'Avant, derrière, sur les côtés.' },
        { label: 'GARDER UNE POSSIBILITÉ D\'ACTION', desc: 'Toujours avoir une option de repli.' },
      ],
      body: 'Tout en respectant les règles de circulation.',
      retenir: 'TON PLACEMENT EST UNE DÉCISION, PAS UNE HABITUDE.',
    },
    {
      num: '07',
      kicker: 'Masques de visibilité',
      title: 'Tu adaptes ta conduite aux masques de visibilité',
      image: '/images/articles/cours3-c7.png',
      imageAlt: 'Tu adaptes ta conduite aux masques de visibilité — Code 2 Roues',
      lead: 'Camionnette stationnée, poids lourd, haie, bâtiment, virage, file de véhicules… Tu dois maintenant apprendre à raisonner sur ce qui peut être présent derrière ce que tu ne vois pas.',
      type: 'doDont',
      dont: {
        title: 'JE ROULE COMME SI JE VOYAIS TOUT',
        desc: 'Confiance excessive, allure inadaptée, aucune marge en cas d\'apparition brutale.',
      },
      doItem: {
        title: 'JE ROULE COMME SI UN DANGER POUVAIT SURGIR',
        desc: 'Allure réduite, écartement, position qui améliore ma vision, prêt à réagir.',
      },
      retenir: 'ABSENCE DE VISIBILITÉ ≠ ABSENCE DE DANGER.',
    },
    {
      num: '08',
      kicker: 'Changements de voie',
      title: 'Tu gères les changements de voie et les insertions',
      image: '/images/articles/cours3-c8.png',
      imageAlt: 'Tu gères les changements de voie et les insertions — Code 2 Roues',
      lead: 'Contrôles, rétroviseurs, angle mort, clignotant, allure, espace disponible et décision. On travaille la séquence :',
      type: 'timeline',
      steps: [
        { time: '1', label: 'J\'OBSERVE', desc: 'Regards et rétroviseurs.' },
        { time: '2', label: 'JE COMMUNIQUE', desc: 'Clignotant clair et précoce.' },
        { time: '3', label: 'JE CONTRÔLE', desc: 'Angles morts, dernière vérification.' },
        { time: '4', label: 'JE DÉCIDE', desc: 'Le moment est-il vraiment le bon ?' },
        { time: '5', label: 'J\'AGIS', desc: 'Manœuvre fluide et maîtrisée.' },
      ],
      retenir: 'L\'OBJECTIF N\'EST PAS SEULEMENT DE CHANGER DE VOIE, MAIS DE SAVOIR CHOISIR LE BON MOMENT.',
    },
    {
      num: '09',
      kicker: 'Dépassement',
      title: 'Tu apprends à dépasser en sécurité',
      image: '/images/articles/cours3-c9.png',
      imageAlt: 'Tu apprends à dépasser en sécurité — Code 2 Roues',
      lead: 'Le dépassement devient une décision construite, jamais une obligation. Avant de dépasser, tu analyses :',
      type: 'list',
      list: [
        { label: 'VISIBILITÉ', desc: 'Vois-tu vraiment loin devant ?' },
        { label: 'SIGNALISATION', desc: 'Ligne, panneaux, priorités.' },
        { label: 'DIFFÉRENCE D\'ALLURE', desc: 'Suffisante pour un dépassement rapide.' },
        { label: 'ENVIRONNEMENT', desc: 'Ville, route, virage, intersection.' },
        { label: 'AUTRES USAGERS', desc: 'Ce qui te suit, ce qui vient.' },
        { label: 'ESPACE DISPONIBLE', desc: 'Devant et derrière le véhicule dépassé.' },
        { label: 'POSSIBILITÉ DE TE RABATTRE', desc: 'En sécurité, sans forcer.' },
      ],
      retenir: 'SI LES CONDITIONS NE SONT PAS RÉUNIES, TU NE DÉPASSES PAS.',
    },
    {
      num: '10',
      kicker: 'Virages',
      title: 'Tu gères les virages avec plus d\'autonomie',
      image: '/images/articles/cours3-c10.png',
      imageAlt: 'Tu gères les virages avec plus d\'autonomie — Code 2 Roues',
      lead: 'Nous faisons évoluer le travail vers la lecture réelle de la route :',
      type: 'timeline',
      steps: [
        { time: 'Avant', label: 'ALLURE ADAPTÉE', desc: 'Réduite avant l\'entrée, pas dans le virage.' },
        { time: 'Entrée', label: 'REGARD ET POSITIONNEMENT', desc: 'Regard porté loin, position d\'attaque du virage.' },
        { time: 'Dans le virage', label: 'TRAJECTOIRE DE SÉCURITÉ', desc: 'Extérieur → intérieur → extérieur, sans surprise.' },
        { time: 'Sortie', label: 'REMISE EN ACCÉLÉRATION', desc: 'Lorsque les conditions le permettent.' },
      ],
      retenir: 'TU APPRENDS À LIRE LE VIRAGE AVANT D\'Y ENTRER.',
    },
    {
      num: '11',
      kicker: 'Environnements',
      title: 'Tu t\'adaptes aux différents environnements',
      image: '/images/articles/cours3-c11.png',
      imageAlt: 'Tu t\'adaptes aux différents environnements — Code 2 Roues',
      lead: 'Ville, route, zones commerciales, circulation dense, chaussée étroite, relief… La même moto et le même motard doivent produire des conduites différentes :',
      type: 'list',
      list: [
        { label: 'REGARD', desc: 'Plus court en ville, plus long sur route.' },
        { label: 'ALLURE', desc: 'Adaptée au niveau d\'imprévisibilité.' },
        { label: 'DISTANCES', desc: 'Plus grandes quand la visibilité diminue.' },
        { label: 'ANTICIPATION', desc: 'Plus soutenue en environnement dense.' },
      ],
      retenir: 'MÊME MOTO, MÊME CONDUCTEUR — CONDUITES DIFFÉRENTES SELON L\'ENVIRONNEMENT.',
    },
    {
      num: '12',
      kicker: 'Décisions',
      title: 'Tu prends tes propres décisions',
      image: '/images/articles/cours3-c12.png',
      imageAlt: 'Tu prends tes propres décisions — Code 2 Roues',
      lead: 'C\'est le véritable changement du cours 3. L\'enseignant peut annoncer une direction : « Au prochain giratoire, direction Lyon. » Mais c\'est à toi de gérer :',
      type: 'list',
      list: [
        { label: 'ALLURE', desc: 'Adaptée à l\'approche du giratoire.' },
        { label: 'CONTRÔLES', desc: 'Regards, rétroviseurs, angles morts.' },
        { label: 'PLACEMENT', desc: 'Voie et position optimales.' },
        { label: 'PRIORITÉ', desc: 'Tu identifies qui a la priorité.' },
        { label: 'ENGAGEMENT', desc: 'Décision claire au bon moment.' },
        { label: 'COMMUNICATION', desc: 'Clignotant, regard, signaux.' },
      ],
      retenir: 'LA RADIO DONNE DE MOINS EN MOINS LA SOLUTION. C\'EST TOI QUI CONSTRUIS LA CONDUITE.',
    },
    {
      num: '13',
      kicker: 'Justifier',
      title: 'Tu expliques et justifies ta conduite',
      image: '/images/articles/cours3-c13.png',
      imageAlt: 'Tu expliques et justifies ta conduite — Code 2 Roues',
      lead: 'Pendant les arrêts pédagogiques ou le débriefing, ton enseignant peut te demander :',
      type: 'list',
      list: [
        { label: '« POURQUOI AS-TU RALENTI ? »', desc: 'Tu identifies ce que tu avais détecté.' },
        { label: '« QU\'AVAIS-TU IDENTIFIÉ ? »', desc: 'Tu nommes les indices.' },
        { label: '« POURQUOI AS-TU CHOISI CETTE POSITION ? »', desc: 'Tu justifies ton placement.' },
        { label: '« POURQUOI N\'AS-TU PAS DÉPASSÉ ? »', desc: 'Tu expliques ton analyse.' },
        { label: '« QU\'AURAIS-TU PU FAIRE AUTREMENT ? »', desc: 'Tu prends du recul.' },
      ],
      retenir: 'NOUS PASSONS DE « JE SAIS FAIRE » À « JE SAIS POURQUOI JE LE FAIS ».',
    },
    {
      num: '14',
      kicker: 'Bilan N°3',
      title: 'Ton bilan n°3 : vers l\'autonomie',
      image: '/images/articles/cours3-c14.png',
      imageAlt: 'Ton bilan n°3 : vers l\'autonomie — Code 2 Roues',
      lead: 'L\'auto-évaluation évolue encore. Est-ce que je sais…',
      type: 'bilan',
      questions: [
        'Chercher les informations loin devant ?',
        'Hiérarchiser les risques ?',
        'Gérer plusieurs informations simultanément ?',
        'Choisir mon positionnement ?',
        'Anticiper un danger masqué ?',
        'Changer de voie en autonomie ?',
        'Décider si un dépassement est possible ?',
        'Adapter ma conduite aux virages et à l\'environnement ?',
        'Prendre une décision sans attendre la radio ?',
        'Expliquer pourquoi j\'ai agi ?',
      ],
      blocks: [
        { title: 'CE QUE JE MAÎTRISE', color: 'green' },
        { title: 'CE QUE JE DOIS CONSOLIDER', color: 'amber' },
        { title: 'MON OBJECTIF POUR LA SESSION N°4', color: 'red' },
      ],
      retenir: 'CONDUIRE, CE N\'EST PLUS SEULEMENT RÉAGIR À CE QUI ARRIVE. C\'EST PRÉPARER CE QUI POURRAIT ARRIVER.',
    },
  ],
};
